import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
/** CHART COMPONENTS */
import CustomTooltip from '../CustomTooltip';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomYAxisTick from '../CustomYAxisTick';
/** UTILS */
import { useYAxisBounds } from '../hooks/useYAxisBounds';
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  CHART_MARGIN,
  DATA_KEY,
  DEFAULT_X_AXIS_CONFIG,
} from '../constants/utils';
import { isNil } from 'lodash';
/** INTERFACES */
import {
  ChartNumberType,
  RetentionPostfixOnColumnName,
  YAxisPosition,
} from '../interfaces';
import {
  useSingleChartDataTooltip,
  usePrepareCohort,
  useRetentionLines,
  useYAxisWidth,
} from '../hooks';
import { QueryType } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import { bootstrapTimezoneSelector } from '../../../redux/selectors/bootstrap';
import { useAppSelector } from '../../../redux/hooks';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useBuilderInfoDisplay from '../utils/useBuilderInfoDisplay';
import { ChartMode } from '../types';
import calculateBarSize from '../utils/calculateBarSize';
import RetentionCellTooltip from '../RetentionChart/RetentionCellTooltip/RetentionCellTooltip';
import { generateRetentionTooltips } from '../RetentionChart/RetentionCellTooltip/RetentionCellTooltip';
import { useTooltipWheelContext } from '../CustomTooltip/CustomTooltipWheelContext';
import { useBarChartOpacity } from '../hooks/useBarChartOpacity';
import useCacheParsedChartLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheParsedChartLlmRawDataEffect';
import { useFloatingLayerContext } from '../../FloatingManager';
import PopperPortal from '../CustomTooltip/PopperPortal';
import { useRef } from 'react';
import { useChartContext } from '../ChartContext';

interface IProps {
  analysisId: string;
  mode: ChartMode;
}

export default ({ analysisId, mode }: IProps) => {
  const { tooltipContentRef } = useTooltipWheelContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);
  const {
    settings: { chartSettings, chartWidth },
    chartData,
    metadata: { isBreakdownOverall },
    displayedSlices,
    colorConfig,
    displayedMeasures,
  } = useChartContext();
  assertCondition(chartData.queryType === QueryType.Retention);
  const { retentionLines, data } = useRetentionLines(
    chartData,
    chartSettings,
    isBreakdownOverall,
    colorConfig,
  );
  const { chartNumberType, chartType, axisSettings } = chartSettings;
  const { linesMeta } = chartData;
  const { getBarOpacity, setActiveLabel } = useBarChartOpacity();
  const { handleCreateCohortFromRetention } = usePrepareCohort({ analysisId });
  const {
    tooltipProps,
    isTooltipLocked,
    onMouseLeave,
    onBarMouseMove,
    clearLockedTooltipProps,
    lockTooltipPositionHandler,
  } = useSingleChartDataTooltip({
    containerRef,
  });

  const { getYAxisDomain } = useYAxisBounds();

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const numItems = retentionLines.length;
  const barSize = calculateBarSize(numItems, chartWidth);

  const { retentionTooltipProps, positionProps } = generateRetentionTooltips({
    tooltipProps,
    linesMeta,
    chartData,
    data,
    chartSettings,
  });

  const tooltipZIndex = useFloatingLayerContext();

  // XXX also cache the raw chart numbers in redux, to be used as a context sent to the LLM
  useCacheParsedChartLlmRawDataEffect({
    analysisId,
    chartTypeName: 'Bar',
    parsedChartDataRecords: data,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
      <ResponsiveContainer ref={containerRef}>
        <BarChart
          data={data}
          margin={CHART_MARGIN}
          barCategoryGap={'12%'}
          onMouseMove={(props) => {
            setActiveLabel(props.activeLabel ?? null);
          }}
          onMouseLeave={() => {
            setActiveLabel(null);
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey={DATA_KEY}
            {...DEFAULT_X_AXIS_CONFIG}
            tick={({ ...props }) => (
              <CustomXAxisTick
                {...props}
                limit={barSize}
                shouldRotate={false}
              />
            )}
          />
          <YAxis
            padding={{ top: 3, bottom: 0 }}
            hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
            tickSize={0}
            domain={getYAxisDomain({
              yAxisPosition: YAxisPosition.LEFT,
            })}
            allowDataOverflow={true}
            axisLine={false} // Whether to display the Y axis vertical line or not
            tick={
              <CustomYAxisTick
                measureDisplayFormat={null}
                forcePercent={chartNumberType === ChartNumberType.Percentage}
              />
            }
            tickCount={5}
            tickMargin={10}
            width={yAxisWidth}
          />
          <Tooltip
            cursor={false}
            offset={20}
            wrapperStyle={{
              zIndex: tooltipZIndex,
              position: mode === ChartMode.Compact ? 'fixed' : 'absolute',
            }}
            allowEscapeViewBox={{ x: false, y: true }}
            content={(props: any) =>
              isNil(retentionTooltipProps) ? (
                <PopperPortal active={props.active}>
                  {CustomTooltip({
                    props,
                    lines: retentionLines,
                    chartType,
                    chartValues: data,
                    linesMeta,
                    timezone: orgTimezone,
                    forceBothValues: true,
                    displayedMeasures,
                    displayedSlices,
                    additionalLabel: builderInfoDisplay,
                    colorConfig,
                    isBreakdownOverall,
                    tooltipPayloadContentRef: tooltipContentRef,
                  })}
                </PopperPortal>
              ) : null
            }
          />
          {retentionLines.map((line, index) => {
            const dataKey =
              chartNumberType === ChartNumberType.Number
                ? line.name
                : line.name + RetentionPostfixOnColumnName.percentKey;
            return (
              <Bar
                isAnimationActive={false}
                key={`line-${index}`}
                name={line.name}
                dataKey={dataKey}
                fill={line.color}
                strokeDasharray={'1 0'}
                minPointSize={BAR_CHART_MIN_POINT_SIZE}
                strokeWidth={BAR_CHART_STROKE_WIDTH}
                barSize={barSize}
                onMouseMove={(eventProps, _, mouseElement) => {
                  onBarMouseMove({
                    rechartProps: { ...eventProps, xAxisKey: DATA_KEY },
                    line,
                    mouseEvent: mouseElement,
                  });
                }}
                onMouseLeave={onMouseLeave}
                onClick={lockTooltipPositionHandler}
              >
                {data.map((entry, index) => {
                  const opacity = getBarOpacity({
                    tooltipProps,
                    xAxisValue: entry[DATA_KEY] as string,
                    lineName: line.name,
                    isTooltipLocked,
                  });
                  return <Cell key={`cell-${index}`} opacity={opacity} />;
                })}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
      {retentionTooltipProps && positionProps && (
        <RetentionCellTooltip
          analysisId={analysisId}
          tooltipProps={retentionTooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
          handleCreateCohort={handleCreateCohortFromRetention}
          position={positionProps}
          cellBackgroundColor={
            retentionLines.find(
              (line) => line.name === retentionTooltipProps?.key,
            )?.color
          }
        />
      )}
    </ResponsiveChartWrapper>
  );
};
